// Şube create/edit formu (MOD-11 / K-2).
//
// react-hook-form + zod. Koordinat ZORUNLU (BIZ-BR-01) — harita-tıkla / marker-sürükle
// VEYA manuel lat/lng, iki yönlü bağlı. İl seçilince ilçe listesi bağımlı yüklenir
// (`GET /cities/{slug}/districts`), il değişince ilçe temizlenir. Harita lazy-load
// (vendor-map chunk). Save butonu sayfa başlığındadır (form={FORM_ID}).

import { lazy, Suspense, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Form as AntForm, Col, Input, InputNumber, Row, Select, Skeleton, Switch } from 'antd';
import { FormProvider, useForm, useWatch, type DefaultValues } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { FormField } from '@shared/components/Form/FormField';
import { SlugInput } from '@shared/components/SlugInput/SlugInput';
import type { Translator } from '@/i18n/config';
import { useQuery } from '@tanstack/react-query';
import { citiesApi } from '@api/branches';
import type { Branch, BranchInput, City } from '../types';

const BranchLocationPicker = lazy(() => import('./BranchLocationPicker'));

export const BRANCH_FORM_ID = 'branch-form';

function makeSchema(t: Translator) {
  return z.object({
    name: z.string().min(1, t('form.nameRequired')).max(200),
    slug: z.string().max(200),
    addressLine: z.string().min(1, t('form.addressRequired')).max(500),
    cityId: z.string().min(1, t('form.cityRequired')),
    districtId: z.string().nullable(),
    latitude: z
      .number({
        invalid_type_error: t('form.coordRequired'),
        required_error: t('form.coordRequired'),
      })
      .finite(t('form.coordRequired'))
      .min(-90, t('form.latRange'))
      .max(90, t('form.latRange')),
    longitude: z
      .number({
        invalid_type_error: t('form.coordRequired'),
        required_error: t('form.coordRequired'),
      })
      .finite(t('form.coordRequired'))
      .min(-180, t('form.lngRange'))
      .max(180, t('form.lngRange')),
    phone: z.string().max(30),
    email: z.union([z.literal(''), z.string().email(t('form.emailInvalid'))]),
    workingHours: z.string().max(2000),
    order: z.number().int().min(0),
    isActive: z.boolean(),
  });
}

type Values = z.infer<ReturnType<typeof makeSchema>>;

interface BranchFormProps {
  branch?: Branch;
  cities: City[];
  citiesLoading: boolean;
  onSubmit: (input: BranchInput) => void;
}

export function BranchForm({ branch, cities, citiesLoading, onSubmit }: BranchFormProps) {
  const { t } = useTranslation(['branches', 'common']);
  const schema = useMemo(() => makeSchema(t as unknown as Translator), [t]);

  const defaultValues: DefaultValues<Values> = {
    name: branch?.name ?? '',
    slug: branch?.slug ?? '',
    addressLine: branch?.addressLine ?? '',
    cityId: branch?.cityId ?? '',
    districtId: branch?.districtId ?? null,
    latitude: branch?.latitude,
    longitude: branch?.longitude,
    phone: branch?.phone ?? '',
    email: branch?.email ?? '',
    workingHours: branch?.workingHours ?? '',
    order: branch?.order ?? 0,
    isActive: branch?.isActive ?? true,
  };

  const methods = useForm<Values>({ resolver: zodResolver(schema), defaultValues, mode: 'onBlur' });
  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors },
  } = methods;

  // useWatch (hook) — doğrudan useForm().watch() yerine (react-hooks/incompatible-library).
  const [nameValue, slug, cityId, districtId, latitude, longitude] = useWatch({
    control,
    name: ['name', 'slug', 'cityId', 'districtId', 'latitude', 'longitude'],
  });

  const citySlug = useMemo(() => cities.find((c) => c.id === cityId)?.slug, [cities, cityId]);

  const { data: districts = [], isLoading: districtsLoading } = useQuery({
    queryKey: ['branch-districts', citySlug],
    queryFn: () => citiesApi.districts(citySlug ?? ''),
    enabled: !!citySlug,
  });

  const setNum = (name: 'latitude' | 'longitude', v: number | null) =>
    setValue(name, (v === null ? undefined : v) as number, {
      shouldValidate: true,
      shouldDirty: true,
    });

  const handlePick = (lat: number, lng: number) => {
    setNum('latitude', lat);
    setNum('longitude', lng);
  };

  const submit = handleSubmit((values) =>
    onSubmit({
      name: values.name.trim(),
      slug: values.slug.trim() || undefined,
      addressLine: values.addressLine.trim(),
      cityId: values.cityId,
      districtId: values.districtId || null,
      latitude: values.latitude,
      longitude: values.longitude,
      phone: values.phone.trim() || null,
      email: values.email.trim() || null,
      workingHours: values.workingHours.trim() || null,
      order: values.order,
      isActive: values.isActive,
    }),
  );

  const coordError = errors.latitude?.message ?? errors.longitude?.message;

  return (
    <FormProvider {...methods}>
      <form id={BRANCH_FORM_ID} onSubmit={(e) => void submit(e)} noValidate>
        <AntForm layout="vertical" component="div">
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <FormField name="name" label={t('form.name')} required>
                <Input placeholder={t('form.namePlaceholder')} maxLength={200} />
              </FormField>
            </Col>
            <Col xs={24} md={12}>
              <AntForm.Item
                label={t('form.slug')}
                validateStatus={errors.slug ? 'error' : undefined}
                help={errors.slug?.message ?? t('form.slugHint')}
              >
                <SlugInput
                  value={slug ?? ''}
                  onChange={(v) => setValue('slug', v, { shouldDirty: true })}
                  sourceValue={nameValue}
                />
              </AntForm.Item>
            </Col>
          </Row>

          <FormField name="addressLine" label={t('form.address')} required>
            <Input.TextArea rows={2} placeholder={t('form.addressPlaceholder')} maxLength={500} />
          </FormField>

          <Row gutter={16}>
            <Col xs={24} md={12}>
              <AntForm.Item
                label={t('form.city')}
                required
                validateStatus={errors.cityId ? 'error' : undefined}
                help={errors.cityId?.message}
              >
                <Select
                  showSearch
                  optionFilterProp="label"
                  placeholder={t('form.cityPlaceholder')}
                  value={cityId || undefined}
                  loading={citiesLoading}
                  onChange={(v: string) => {
                    setValue('cityId', v, { shouldValidate: true, shouldDirty: true });
                    setValue('districtId', null, { shouldDirty: true });
                  }}
                  options={cities.map((c) => ({
                    value: c.id,
                    label: `${c.plateCode} · ${c.name}`,
                  }))}
                  data-testid="branch-city"
                />
              </AntForm.Item>
            </Col>
            <Col xs={24} md={12}>
              <AntForm.Item label={t('form.district')} help={t('form.districtHint')}>
                <Select
                  showSearch
                  allowClear
                  optionFilterProp="label"
                  placeholder={t('form.districtPlaceholder')}
                  value={districtId ?? undefined}
                  disabled={!cityId}
                  loading={districtsLoading}
                  onChange={(v?: string) =>
                    setValue('districtId', v ?? null, { shouldDirty: true })
                  }
                  options={districts.map((d) => ({ value: d.id, label: d.name }))}
                  data-testid="branch-district"
                />
              </AntForm.Item>
            </Col>
          </Row>

          <AntForm.Item
            label={t('form.location')}
            required
            validateStatus={coordError ? 'error' : undefined}
            help={coordError ?? t('form.locationHint')}
          >
            <Suspense fallback={<Skeleton.Node active style={{ width: '100%', height: 320 }} />}>
              <BranchLocationPicker
                latitude={latitude ?? null}
                longitude={longitude ?? null}
                onChange={handlePick}
              />
            </Suspense>
            <Row gutter={16} style={{ marginTop: 12 }}>
              <Col xs={12}>
                <InputNumber
                  style={{ width: '100%' }}
                  value={latitude ?? null}
                  onChange={(v) => setNum('latitude', v)}
                  placeholder={t('form.latitude')}
                  addonBefore={t('form.latitude')}
                  step={0.000001}
                  data-testid="branch-lat"
                />
              </Col>
              <Col xs={12}>
                <InputNumber
                  style={{ width: '100%' }}
                  value={longitude ?? null}
                  onChange={(v) => setNum('longitude', v)}
                  placeholder={t('form.longitude')}
                  addonBefore={t('form.longitude')}
                  step={0.000001}
                  data-testid="branch-lng"
                />
              </Col>
            </Row>
          </AntForm.Item>

          <Row gutter={16}>
            <Col xs={24} md={12}>
              <FormField name="phone" label={t('form.phone')}>
                <Input placeholder={t('form.phonePlaceholder')} maxLength={30} />
              </FormField>
            </Col>
            <Col xs={24} md={12}>
              <FormField name="email" label={t('form.email')}>
                <Input placeholder={t('form.emailPlaceholder')} maxLength={120} />
              </FormField>
            </Col>
          </Row>

          <FormField
            name="workingHours"
            label={t('form.workingHours')}
            help={t('form.workingHoursHint')}
          >
            <Input.TextArea
              rows={2}
              placeholder={t('form.workingHoursPlaceholder')}
              maxLength={2000}
            />
          </FormField>

          <Row gutter={16}>
            <Col xs={12} md={6}>
              <FormField name="order" label={t('form.order')} help={t('form.orderHint')}>
                <InputNumber style={{ width: '100%' }} min={0} />
              </FormField>
            </Col>
            <Col xs={12} md={6}>
              <FormField name="isActive" label={t('form.isActive')} valueProp="checked">
                <Switch />
              </FormField>
            </Col>
          </Row>
        </AntForm>
      </form>
    </FormProvider>
  );
}
